import { useMemo } from "react";

import { StructuredSearchProvider, StructuredSearchbar } from "@/components/StructuredSearch";
import { tracesSearchSchema } from "@/components/StructuredSearch/schemas/traces-search-schema";
import TracesAgGrid from "@/components/TracesSearch/TracesAgGrid";
import type { HistoryImportConfig } from "@/components/ui/ag-grid-table";
import { TracesTableRefreshProvider } from "@/components/TracesSearch/TracesAgGrid/traces-table-refresh-context";
import { TimezoneProvider } from "@/context/timezone-context";
import { useGetImportedSourceIds } from "@/queries/smart-tables";
import type { SmartTableTraceImportPayload } from "@/queries/smart-tables/useSmartTableTraceImports";
import { buildHistoryImportExclusionLockedFilters } from "@/routes/smart-table/utils/buildHistoryImportExclusionLockedFilters";
import { HistoryImportSource } from "@/routes/smart-table/utils/historyImportTypes";

interface TraceHistoryImportContentProps {
  importConfig: HistoryImportConfig<SmartTableTraceImportPayload>;
  tableId?: string;
  sheetId?: string;
  targetColumnId?: string | null;
}

const TraceHistoryImportContent = ({
  importConfig,
  tableId,
  sheetId,
  targetColumnId,
}: TraceHistoryImportContentProps) => {
  const { data: importedIds = [], isLoading } = useGetImportedSourceIds({
    tableId,
    sheetId,
    source: "trace",
    columnId: targetColumnId,
    enabled: Boolean(tableId && sheetId),
  });

  const exclusionFilters = useMemo(
    () =>
      buildHistoryImportExclusionLockedFilters({
        source: HistoryImportSource.Trace,
        importedIds,
      }),
    [importedIds],
  );

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
        Loading imported traces…
      </div>
    );
  }

  const providerKey = [
    "trace-import",
    targetColumnId ?? "sheet",
    importedIds.length,
    importedIds[0] ?? "",
    importedIds[importedIds.length - 1] ?? "",
  ].join(":");

  return (
    <StructuredSearchProvider
      key={providerKey}
      config={tracesSearchSchema}
      initialEditableFilters={exclusionFilters}
      initialUrlHydrationReady
    >
      <TimezoneProvider>
        <TracesTableRefreshProvider>
          <div className="flex h-full w-full flex-col bg-white">
            <div className="flex-shrink-0 overflow-visible">
              <div className="py-4">
                <StructuredSearchbar className="w-full" title="Traces" />
              </div>
            </div>
            <TracesAgGrid traceImportConfig={importConfig} />
          </div>
        </TracesTableRefreshProvider>
      </TimezoneProvider>
    </StructuredSearchProvider>
  );
};

export default TraceHistoryImportContent;
